/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-max-depth */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Grid, Hidden } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { Navigation } from "./navigation/Navigation";
import { Profile } from "./profile/Profile";
import { AppState } from "../../../redux";
import { getFeatureFlags } from "../../../redux/featureFlagging/actions";
import { getMetaData } from "../../../redux/metaData/actions";
import { getSecuritySettings } from "../../../redux/security/password-policy/actions";
import routes from "../../../routing/routes";
import TopNavAndAccessibility from "./TopNavAndAccessibility";
import LeavePageDialog from "../dialog/LeavePageDialog";
import Loader from "../../theming/loader/Loader";
import "./Header.scss";

const logoLinkHref = process.env.REACT_APP_LOGO_LINK_HREF ?? "/";

const extensions = ["png", "svg"];
let logo: any;

for (const ext of extensions) {
  try {
    logo = require(`../../../assets/general/customer_header.${ext}`);
    break;
  } catch (e) {
    // eslint-disable no-empty
  }
}

if (!logo) {
  throw new Error("Logo File not found");
}

/**
 * Global application header component
 *
 * Renders logo, profile navigation, accessibility bar,
 * feature flag and security settings loading, and customer metadata loading.
 */
export const Header: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation(["layout", "snackbars"]);

  const {
    accessibility,
    auth,
    user,
    securitySettings,
    featureFlagging: featureFlags,
    metaData,
  } = useSelector((state: AppState) => state);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!securitySettings.isLoading && !securitySettings.isLoaded) {
      dispatch(getSecuritySettings());
    }
  }, [securitySettings]);

  const handleConfirm = () => {
    setDialogOpen(false);
    if (logoLinkHref) {
      window.open(logoLinkHref, "_blank", "noopener,noreferrer");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const [customer, setCustomer] = useState("");

  useEffect(() => {
    if (!metaData.metaDataLoaded && !metaData.isLoading) {
      dispatch(getMetaData());
    }
  }, [metaData, dispatch]);

  useEffect(() => {
    if (metaData.metaData.customer && customer.length === 0) {
      setCustomer(metaData.metaData.customer);
    }
  }, [metaData.metaData.customer, customer]);

  useEffect(() => {
    if (!featureFlags.isLoading && !featureFlags.featureFlagsLoaded) {
      dispatch(getFeatureFlags());
    }
  }, [featureFlags]);

  const contentText = t("dialogues.leavePage", {
    linkHref: logoLinkHref,
    customer: customer,
  });

  if (customer.length === 0 && metaData.isLoading) {
    return <Loader />;
  }

  const hideBurgerMenu = !user?.currentUser?.terms_agreed_at;

  return (
    <header className="box-shadow">
      <Container>
        <Grid container>
          <Hidden smUp>
            <Grid item xs={12}>
              <TopNavAndAccessibility />
            </Grid>
          </Hidden>

          <Grid item xs={12}>
            <Box display="flex">
              {logo ? (
                <Button
                  className="navbar-logo-button"
                  style={{ backgroundColor: "transparent" }}
                  onClick={handleOpenDialog}
                >
                  <img
                    src={logo}
                    className={
                      accessibility.monoMode
                        ? "navbar-header-logo-mono"
                        : "navbar-header-logo"
                    }
                    alt="logo"
                  />
                </Button>
              ) : null}
              <LeavePageDialog
                content={contentText}
                open={dialogOpen}
                onClose={handleCloseDialog}
                onConfirm={handleConfirm}
              />
              {/* Responsive */}
              <Box flexGrow={1}>
                <Hidden xsDown>
                  <Grid item xs={12}>
                    <TopNavAndAccessibility />
                  </Grid>
                </Hidden>
                <Box display="flex" justifyContent="flex-end">
                  {auth.loggedIn ? (
                    user.isLoading ? (
                      <Box display="flex" alignItems="center">
                        <Box display="flex" flexDirection="column" mr="1rem">
                          <Skeleton width={100} height={18} />
                          <Skeleton width={100} height={24} />
                        </Box>
                        <Skeleton variant="circle" width={50} height={50} />
                      </Box>
                    ) : (
                      <>
                        <Hidden smDown>{!hideBurgerMenu && <Profile />}</Hidden>
                        {!hideBurgerMenu && <Navigation />}
                      </>
                    )
                  ) : (
                    location.pathname !== routes.login && (
                      <Button
                        id="submit-login-profile"
                        style={{
                          height: "3.875rem",
                          padding: "0.625rem",
                          fontSize: "1rem",
                          position: "relative",
                          top: ".25rem",
                        }}
                        size="large"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          history.push(routes.login);
                        }}
                      >
                        {t("header.login")}
                      </Button>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </header>
  );
};
